import { type MutationKey, type UseMutationResult, useMutation } from "@tanstack/react-query"
import { AxiosError, type AxiosResponse } from "axios"

import { isInternalServerError } from "@/3514/utils"
import { seconds } from "@/3514/utils/time.utils"
import { type TEmptyAsyncCallback } from "@/shared/types/functions"

import {
    EParticipantOnboardingMutationKey as EKey,
    EParticipantOnboardingStep as EStep
} from "./participant.onboarding.api.config"
import mutators from "./participant.onboarding.api.mutations.mutators"
import type {
    TParticipantOnboardingCoachStepPairingMutationPayload as TCoachStepPairingPayload,
    TParticipantOnboardingMutationMap as TMutationMap,
    TParticipantOnboardingMutationOptions as TMutationOptions,
    TParticipantOnboardingMutationResponse as TMutationResponse,
    TParticipantOnboardingMutationPayloadMap as TPayloadMap
} from "./participant.onboarding.api.types"

type TOnboardingMutation<TStep extends EStep = EStep> = UseMutationResult<
    TMutationResponse,
    AxiosError,
    TPayloadMap[TStep],
    void
>

const stepKeysMap: { [S in EStep]: MutationKey } = {
    [EStep.Profile]: EKey.Profile.NextStep(),
    [EStep.Video]: EKey.Video.NextStep(),
    [EStep._LegacyModules]: EKey._LegacyModules.NextStep(),
    [EStep.Diagnostic]: EKey.Diagnostic.NextStep(),
    [EStep._LegacyDiagnosticResponses]: EKey._LegacyDiagnosticResponses.NextStep(),
    [EStep.Reflection]: EKey.Reflection.NextStep(),
    [EStep.Holds]: EKey.Holds.NextStep(),
    [EStep.Coach]: EKey.Holds.NextStep(),
    [EStep.Final]: null
}

function useOnboardingMutation<TStep extends EStep>(options: TMutationOptions<TStep>): TOnboardingMutation<TStep> {
    return useMutation<TMutationResponse, AxiosError, TPayloadMap[TStep], void>({
        mutationKey: stepKeysMap[options.step],
        mutationFn: mutators.nextStep<TStep>(options.step),
        gcTime: 0,
        retry: (failureCount: number, error: AxiosError): boolean =>
            isInternalServerError(error) ? failureCount <= 1 : false,
        retryDelay: seconds(5),
        networkMode: "online",
        throwOnError: false,
        ...options
    })
}

// coach step specific --start
type TOptions = { onSettled?: TEmptyAsyncCallback }
type TCoachStepPairingMutation = UseMutationResult<AxiosResponse, AxiosError, TCoachStepPairingPayload[], void>
function useCoachStepPairingMutation(options?: TOptions): ReturnType<(options: TOptions) => TCoachStepPairingMutation> {
    return useMutation<AxiosResponse, AxiosError, TCoachStepPairingPayload[], void>({
        mutationKey: EKey.Coach.Pairing(),
        mutationFn: mutators.coachStepPairing,
        gcTime: 0,
        retry: 1,
        retryDelay: seconds(5),
        networkMode: "online",
        throwOnError: false,
        ...options
    })
}
// coach step specific --end

const mutationsMap: TMutationMap = {
    [EStep.Profile]: [useOnboardingMutation<EStep.Profile>],
    [EStep.Video]: [useOnboardingMutation<EStep.Video>],
    [EStep._LegacyModules]: [useOnboardingMutation<EStep._LegacyModules>],
    [EStep.Diagnostic]: [useOnboardingMutation<EStep.Diagnostic>],
    [EStep._LegacyDiagnosticResponses]: [useOnboardingMutation<EStep._LegacyDiagnosticResponses>],
    [EStep.Reflection]: [useOnboardingMutation<EStep.Reflection>],
    [EStep.Holds]: [useOnboardingMutation<EStep.Holds>],
    [EStep.Coach]: [useOnboardingMutation<EStep.Coach>, useCoachStepPairingMutation],
    [EStep.Final]: []
}

export default { mutationsMap }
export type {
    TOnboardingMutation as TParticipantOnboardingMutation,
    TCoachStepPairingMutation as TParticipantOnboardingCoachStepPairingMutation
}
